import { useAuth } from '@clerk/react';
import {
  ChartColumn,
  ChevronRight,
  ClipboardCheck,
  LayoutDashboard,
  MessageSquare,
  MessageSquareDot,
  Rss,
  Users,
} from 'lucide-react';
import { type ComponentType, useState } from 'react';
import {
  LinkBase,
  useMatch,
  useResourceDefinitions,
  useTranslate,
} from 'ra-core';
import { useGetTotalUnreadMessageCount } from '../api/hooks/sendbird/useGetTotalUnreadMessageCount';
import { useHasPermission } from '../auth/useHasPermission';
import { ResourceMenuItem } from '../components/admin/app-sidebar';
import { useTenantId } from '../components/OrganizationGuard';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '../components/ui/sidebar';
import { CustomResourcesValue } from '../resources/resources';

/**
 * The app navigation: Analytics group, Chat, then every resource with a list, in declaration order.
 * Custom-route items need the same permission gate as their route in `AdminResources.tsx`.
 */
export const AppSidebar = () => {
  const isAdmin = useHasPermission('ADMIN_ACCESS');
  const hasChatAccess = useHasPermission('CHAT_ACCESS');
  const resources = useResourceDefinitions();
  const translate = useTranslate();
  const closeMobileSidebar = useCloseMobileSidebar();

  return (
    <Sidebar variant="floating" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              render={<LinkBase to="/" onClick={closeMobileSidebar} />}
              className="data-[slot=sidebar-menu-button]:!p-1.5"
            >
              <img
                src="/favicon.svg"
                alt=""
                className="h-5 w-auto shrink-0 object-contain"
              />
              <span className="text-base font-semibold">
                {translate('moonstar.menu.brand')}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {isAdmin && <AnalyticsMenuItem />}
              {hasChatAccess && <ChatMenuItem />}
              {Object.keys(resources)
                .filter((name) => resources[name]?.hasList)
                .map((name) => (
                  <ResourceMenuItem
                    key={name}
                    name={name}
                    onClick={closeMobileSidebar}
                  />
                ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

const useCloseMobileSidebar = () => {
  const { openMobile, setOpenMobile } = useSidebar();
  return () => {
    if (openMobile) {
      setOpenMobile(false);
    }
  };
};

const AnalyticsMenuItem = () => {
  const translate = useTranslate();
  const hasDashboardAccess = useHasPermission('ANALYTICS_DASHBOARD_ACCESS');
  const isOnAnalyticsPage = !!useMatch({ path: '/dashboard', end: false });
  const [open, setOpen] = useState(isOnAnalyticsPage);
  const { state, setOpen: setSidebarOpen } = useSidebar();
  const { getToken } = useAuth();
  const tenantId = useTenantId();
  const closeMobileSidebar = useCloseMobileSidebar();

  const handleDashboardClick = async () => {
    const token = await getToken();
    window.open(
      `https://analytics.xservices.ro/${tenantId}/auth/moonstar/redirect?token=${token}`,
      '_blank'
    );
  };

  const handleOpenChange = (nextOpen: boolean) => {
    // The sub menu is hidden in the icon-only sidebar, so expand the sidebar to show it
    if (state === 'collapsed') {
      setSidebarOpen(true);
      setOpen(true);
      return;
    }
    setOpen(nextOpen);
  };

  return (
    <SidebarMenuItem>
      <Collapsible open={open} onOpenChange={handleOpenChange}>
        <CollapsibleTrigger render={<SidebarMenuButton />}>
          <ChartColumn />
          <span>{translate('moonstar.analytics.name')}</span>
          <ChevronRight className="ml-auto transition-transform group-data-[panel-open]/menu-button:rotate-90" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {hasDashboardAccess && (
              <SidebarMenuSubItem>
                <SidebarMenuSubButton
                  render={<button type="button" />}
                  className="w-full cursor-pointer"
                  onClick={() => {
                    closeMobileSidebar();
                    void handleDashboardClick();
                  }}
                >
                  <LayoutDashboard />
                  <span>{translate('moonstar.menu.dashboard')}</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            )}
            <AnalyticsSubMenuItem
              to={`/${CustomResourcesValue.DASHBOARD_USERS}`}
              icon={Users}
              label={translate('moonstar.menu.users')}
              onClick={closeMobileSidebar}
            />
            <AnalyticsSubMenuItem
              to={`/${CustomResourcesValue.DASHBOARD_FEED}`}
              icon={Rss}
              label={translate('moonstar.menu.feed')}
              onClick={closeMobileSidebar}
            />
            <AnalyticsSubMenuItem
              to={`/${CustomResourcesValue.DASHBOARD_PULSE_CHECKS}`}
              icon={ClipboardCheck}
              label={translate('moonstar.menu.pulseChecks')}
              onClick={closeMobileSidebar}
            />
          </SidebarMenuSub>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuItem>
  );
};

const AnalyticsSubMenuItem = ({
  to,
  icon: Icon,
  label,
  onClick,
}: {
  to: string;
  icon: ComponentType;
  label: string;
  onClick: () => void;
}) => {
  const match = useMatch({ path: to, end: false });

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton
        render={<LinkBase to={to} onClick={onClick} />}
        isActive={!!match}
      >
        <Icon />
        <span>{label}</span>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
};

const ChatMenuItem = () => {
  const translate = useTranslate();
  const to = `/${CustomResourcesValue.CHAT}`;
  const match = useMatch({ path: to, end: false });
  const { data: unreadCount } = useGetTotalUnreadMessageCount();
  const hasUnreadMessages = !!unreadCount;
  const closeMobileSidebar = useCloseMobileSidebar();

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={<LinkBase to={to} onClick={closeMobileSidebar} />}
        isActive={!!match}
      >
        {hasUnreadMessages ? (
          <MessageSquareDot
            className="text-sidebar-primary"
            aria-label={translate('moonstar.menu.unreadMessages')}
          />
        ) : (
          <MessageSquare />
        )}
        <span>{translate('moonstar.menu.chat')}</span>
      </SidebarMenuButton>
      {hasUnreadMessages && <SidebarMenuBadge>{unreadCount}</SidebarMenuBadge>}
    </SidebarMenuItem>
  );
};
